import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { motion } from 'framer-motion';
import type React from 'react';
import { AUTOCAPTURE_TIMER_INTERVAL } from '../../../../constants';
import type { AutoCaptureKind } from '../../types';
type OverlayProps = {
  width: number; // Fits the size of video
  height: number;
  videoHeight: number;
  captureKind: AutoCaptureKind;
  outlineWidth: number;
  outlineHeight: number;
  timerAnimationVal?: number;
};
type CornerOutlineProps = {
  width: number;
  height: number;
  outlineWidth: number;
  outlineHeight: number;
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
};
const NUM_MS_IN_SEC = 1000;
const getCornerStyles = (corner: string, outlineWidth: number, outlineHeight: number): React.CSSProperties => {
  const cornerWidth = outlineWidth / 4;
  const cornerHeight = outlineHeight / 4;
  const borderWidth = '4px';
  const borderColor = 'white';
  const borderRadius = '12px';
  return {
    content: "' '",
    position: 'absolute',
    width: `${cornerWidth}px`,
    height: `${cornerHeight}px`,
    top: corner === 'top-left' || corner === 'top-right' ? 0 : 'auto',
    left: corner === 'top-left' || corner === 'bottom-left' ? 0 : 'auto',
    right: corner === 'top-right' || corner === 'bottom-right' ? 0 : 'auto',
    bottom: corner === 'bottom-left' || corner === 'bottom-right' ? 0 : 'auto',
    borderRadius: corner === 'top-left' ? `${borderRadius} 0 0 0` : corner === 'top-right' ? `0 ${borderRadius} 0 0` : corner === 'bottom-right' ? `0 0 ${borderRadius} 0` : `0 0 0 ${borderRadius}`,
    borderTop: corner === 'top-left' || corner === 'top-right' ? `${borderWidth} solid ${borderColor}` : 0,
    borderLeft: corner === 'top-left' || corner === 'bottom-left' ? `${borderWidth} solid ${borderColor}` : 0,
    borderRight: corner === 'top-right' || corner === 'bottom-right' ? `${borderWidth} solid ${borderColor}` : 0,
    borderBottom: corner === 'bottom-left' || corner === 'bottom-right' ? `${borderWidth} solid ${borderColor}` : 0
  };
};
const CornerOutline = t0 => {
  const $ = _c(14);
  const {
    width,
    height,
    outlineWidth,
    outlineHeight,
    corner
  } = t0;
  const t1 = `calc(${height / 2}px - ${outlineHeight / 2}px)`;
  const t2 = `calc(${width / 2}px - ${outlineWidth / 2}px)`;
  const t3 = `${outlineHeight}px`;
  const t4 = `${outlineWidth}px`;
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = {
      top: t1,
      left: t2,
      height: t3,
      width: t4
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== corner || $[6] !== outlineHeight || $[7] !== outlineWidth) {
    t6 = getCornerStyles(corner, outlineWidth, outlineHeight);
    $[5] = corner;
    $[6] = outlineHeight;
    $[7] = outlineWidth;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <div style={t6} />;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div className="absolute" style={t5}>{t7}</div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
type FullFrameOutlineProps = {
  width: number;
  height: number;
  outlineWidth: number;
  outlineHeight: number;
  showBorder?: boolean;
  children?: React.ReactNode;
};
const FullFrameOutline = t0 => {
  const $ = _c(11);
  const {
    width,
    height,
    outlineWidth,
    outlineHeight,
    showBorder,
    children
  } = t0;
  const t1 = showBorder && "border-4 border-white";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("absolute rounded-xl shadow-[0_0_0_1000px_rgba(0,0,0,0.6)]", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `calc(${height / 2}px - ${outlineHeight / 2}px)`;
  const t4 = `calc(${width / 2}px - ${outlineWidth / 2}px)`;
  const t5 = `${outlineHeight}px`;
  const t6 = `${outlineWidth}px`;
  let t7;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = {
      top: t3,
      left: t4,
      height: t5,
      width: t6
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== children || $[8] !== t2 || $[9] !== t7) {
    t8 = <div className={t2} style={t7}>{children}</div>;
    $[7] = children;
    $[8] = t2;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
};
type TimerAnimationProps = {
  timerAnimationVal: number;
};
const TimerAnimation = t0 => {
  const $ = _c(5);
  const {
    timerAnimationVal
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: [1, 0]
    };
    t3 = {
      type: "tween",
      duration: AUTOCAPTURE_TIMER_INTERVAL / NUM_MS_IN_SEC
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== timerAnimationVal) {
    t4 = <motion.div key={timerAnimationVal} className="w-full h-full absolute top-0 rounded-xl bg-black/50" initial={t1} animate={t2} transition={t3} />;
    $[3] = timerAnimationVal;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const Overlay = t0 => {
  const $ = _c(22);
  const {
    width,
    height,
    videoHeight,
    captureKind,
    outlineWidth,
    outlineHeight,
    timerAnimationVal
  } = t0;
  const t1 = `${width}px`;
  const t2 = `${videoHeight}px`;
  const t3 = `calc(-1 * ${videoHeight}px)`;
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = {
      width: t1,
      height: t2,
      marginTop: t3
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== captureKind || $[5] !== height || $[6] !== outlineHeight || $[7] !== outlineWidth || $[8] !== timerAnimationVal || $[9] !== width) {
    t5 = captureKind === "face" && <><FullFrameOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight}>{timerAnimationVal ? <TimerAnimation timerAnimationVal={timerAnimationVal} /> : null}</FullFrameOutline><CornerOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight} corner="top-left" /><CornerOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight} corner="top-right" /><CornerOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight} corner="bottom-left" /><CornerOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight} corner="bottom-right" /></>;
    $[4] = captureKind;
    $[5] = height;
    $[6] = outlineHeight;
    $[7] = outlineWidth;
    $[8] = timerAnimationVal;
    $[9] = width;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== captureKind || $[12] !== height || $[13] !== outlineHeight || $[14] !== outlineWidth || $[15] !== timerAnimationVal || $[16] !== width) {
    t6 = (captureKind === "idDoc" || captureKind === "nonIdDoc") && <FullFrameOutline width={width} height={height} outlineWidth={outlineWidth} outlineHeight={outlineHeight} showBorder={true}>{timerAnimationVal ? <TimerAnimation timerAnimationVal={timerAnimationVal} /> : null}</FullFrameOutline>;
    $[11] = captureKind;
    $[12] = height;
    $[13] = outlineHeight;
    $[14] = outlineWidth;
    $[15] = timerAnimationVal;
    $[16] = width;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="relative overflow-hidden rounded" style={t4}>{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
export default Overlay;